"use client";

import { cn } from "@/lib/utils";
import PrimaryButton from "../ui/primary-button";
import ChipFilterGroup from "../ui/chip-filter-group";
import { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { IconName } from "@/lib/get-icon";
import { useWindowSize } from "@/hooks/use-window-size";
import { handleSmoothScroll } from "@/lib/utils/smooth-scroll";

export interface KeyFeaturesProps {
  id?: string;
  heading?: string;
  buttonCta?: {
    label: string;
    link: string;
  };
  className?: string;
  defaultActiveId?: string;
  items: {
    id: string;
    label: string;
    value: string;
    desktopVideo: string;
    mobileVideo: string;
    leadingIcon: IconName;
    trailingIcon: IconName;
  }[];
}

const KeyFeatures = ({
  id,
  heading = "",
  buttonCta = {
    label: "Request a Demo",
    link: "#",
  },
  className = "",
  defaultActiveId = "",
  items = [],
}: KeyFeaturesProps) => {
  const [activeItem, setActiveItem] = useState<string>(
    defaultActiveId || (items[0]?.id ?? "")
  );
  const playerRefs = useRef<{ [key: string]: ReactPlayer | null }>({});
  const size = useWindowSize();
  const isMobile = size !== "lg" && size !== "md";

  const handleChange = (value: string | number | undefined) => {
    // Reset both desktop and mobile players for the current active item
    const currentDesktopPlayer = playerRefs.current[`${activeItem}-desktop`];
    const currentMobilePlayer = playerRefs.current[`${activeItem}-mobile`];

    if (currentDesktopPlayer) {
      currentDesktopPlayer.seekTo(0);
    }
    if (currentMobilePlayer) {
      currentMobilePlayer.seekTo(0);
    }

    setActiveItem(value as string);
  };

  // Preload all videos
  useEffect(() => {
    items?.forEach((item) => {
      [item.desktopVideo, item.mobileVideo].forEach((videoUrl) => {
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "video";
        link.href = videoUrl;
        document.head.appendChild(link);
      });
    });
  }, [items]);

  return (
    <section
      id={id}
      className={cn(className)}
    >
      <div className="flex w-full flex-col px-4 py-8 font-montserrat md:px-6 md:py-10 lg:mx-auto lg:max-w-lg lg:px-3">
        <div className="flex flex-col items-center justify-center gap-6 text-center md:gap-8">
          <div className="flex flex-col items-center justify-center gap-4 md:gap-8">
            <h2 className="text-heading-4 text-secondary-500 md:text-heading-3 lg:text-heading-2">
              {heading}
            </h2>
            {buttonCta && (
              <PrimaryButton
                className="w-fit"
                href={buttonCta.link}
                onClick={(
                  e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
                ) =>
                  handleSmoothScroll(
                    e as React.MouseEvent<HTMLAnchorElement>,
                    buttonCta.link
                  )
                }
              >
                {buttonCta.label}
              </PrimaryButton>
            )}
          </div>
          <div className="flex w-full flex-col items-center justify-center gap-6">
            <ChipFilterGroup
              containerClassName="flex max-sm:w-full sm:w-full max-md:w-fit justify-center"
              className="gap-2 md:gap-6"
              size="md"
              items={items}
              onChange={handleChange}
              defaultActiveId={defaultActiveId}
            />
            <div
              className={cn("mx-auto w-full", {
                "md:h-[434px] md:w-[696px] lg:h-[624px] lg:w-[1000px]":
                  !isMobile,
                "relative h-[486px] w-[235px]": isMobile,
              })}
            >
              {items?.map((item) => (
                <div
                  key={item.id}
                  className={`h-full w-full ${activeItem === item.id ? "block" : "hidden"}`}
                >
                  {/* Desktop/Tablet Player */}
                  {!isMobile && (
                    <ReactPlayer
                      ref={(player: ReactPlayer | null) => {
                        playerRefs.current[`${item.id}-desktop`] = player;
                      }}
                      url={item.desktopVideo}
                      playing={activeItem === item.id}
                      loop
                      muted
                      width="100%"
                      height="100%"
                      style={{
                        borderRadius: "1.5rem",
                        boxShadow:
                          "4px 8px 32px 0px #FFD5BF99, -4px -8px 32px 0px #C4F4F599",
                      }}
                      config={{
                        file: {
                          attributes: {
                            style: {
                              objectFit: "fill",
                              width: "100%",
                              height: "100%",
                              borderRadius: "1.5rem",
                            },
                          },
                        },
                      }}
                    />
                  )}

                  {/* Mobile Player with iPhone Frame */}
                  {isMobile && (
                    <div className="relative h-[486px] w-[235px] overflow-hidden">
                      {/* iPhone Frame */}
                      <img
                        src="https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F3842c1e4f1df4ec5a628ca9a477674e7"
                        alt="iPhone frame"
                        className="pointer-events-none absolute inset-0 z-10 h-full w-full object-cover md:hidden"
                      />
                      {/* Video Player positioned inside the frame */}
                      <div className="absolute left-[9.5px] top-[20px] z-0 h-[450px] w-[216px] overflow-hidden rounded-[32px]">
                        <ReactPlayer
                          ref={(player: ReactPlayer | null) => {
                            playerRefs.current[`${item.id}-mobile`] = player;
                          }}
                          url={item.mobileVideo}
                          playing={activeItem === item.id}
                          loop
                          muted
                          width="100%"
                          height="100%"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KeyFeatures;
